import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui/button';
import ConfirmationDialog from './ui/ConfirmationDialog';
import { useToast } from './Toast';
import { ApiService } from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { formatDateTime } from '../utils/datetime';
import { Clock } from 'lucide-react';

const PAYMENT_LABELS = { paymentMethod: 'Method', transactionId: 'Transaction ID', remark: 'Remark' };

// Attendee edit from "My registration" that costs more; nothing changes until approved here.
const PendingChangePanel = ({ eventId, userId, pendingChange, currency, onDone }) => {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const settle = (message) => {
    queryClient.invalidateQueries({ queryKey: ['registration', eventId, userId] });
    queryClient.invalidateQueries({ queryKey: ['registrations', eventId] });
    queryClient.invalidateQueries({ queryKey: ['registrationHistory', eventId, userId] });
    showSuccess(message);
    onDone?.();
  };

  const approve = useMutation({
    mutationFn: () => ApiService.approvePendingChange(eventId, userId),
    onSuccess: () => settle('Change approved and the attendee was emailed the updated confirmation.'),
    onError: (err) => showError(err?.message || 'Failed to approve the change.'),
  });

  const reject = useMutation({
    mutationFn: () => ApiService.rejectPendingChange(eventId, userId, reason),
    onSuccess: () => {
      setRejectOpen(false);
      setReason('');
      settle('Change request rejected.');
    },
    onError: (err) => showError(err?.message || 'Failed to reject the change.'),
  });

  const busy = approve.isPending || reject.isPending;
  const payment = pendingChange.paymentData || {};

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center font-semibold text-amber-900">
            <Clock className="w-4 h-4 mr-2" />
            Attendee requested a change · {formatCurrency(pendingChange.amountDue, currency)} extra
          </div>
          <div className="text-xs text-amber-800">Requested {formatDateTime(pendingChange.requestedAt)}</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={busy}
            onClick={() => approve.mutate()}>
            Approve payment & apply
          </Button>
          <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" disabled={busy}
            onClick={() => setRejectOpen(true)}>
            Reject
          </Button>
        </div>
      </div>
      <ul className="divide-y divide-amber-100 rounded border border-amber-200 bg-white text-sm">
        {(pendingChange.changes || []).map((change) => (
          <li key={change.label} className="px-3 py-1.5">
            <span className="font-medium">{change.label}: </span>
            <span className="line-through text-gray-500">{change.from || '—'}</span>
            <span className="mx-1">→</span>
            <span>{change.to || '—'}</span>
          </li>
        ))}
      </ul>
      <div className="text-sm text-amber-900 flex flex-wrap gap-x-4 gap-y-1">
        {Object.entries(PAYMENT_LABELS).filter(([key]) => payment[key]).map(([key, label]) => (
          <span key={key}>{label}: <b>{String(payment[key])}</b></span>
        ))}
        {payment.documentId && <span>Payment proof is in Documents below.</span>}
      </div>
      <ConfirmationDialog
        isOpen={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={() => reject.mutate()}
        title="Reject this change?"
        message="The attendee keeps their current details and gets an email."
        confirmText="Reject"
        isLoading={reject.isPending}
        inputLabel="Note to the attendee (optional)"
        inputValue={reason}
        onInputChange={setReason}
      />
    </div>
  );
};

export default PendingChangePanel;
